import { type FC } from 'react';
import { Info, RectangleEllipsis } from 'lucide-react';
import { format } from 'date-fns';
import { ulid } from 'ulid';
import { Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { FieldComponents } from '@/components/inspector/metadata-accordion/metadata-field-components';
import { useMetadataPanelForm } from '@/components/inspector/metadata-accordion/metadata-panel-form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { type MetadataField, type PublicMetadataField } from '@/types/metadata';
import { DateTimeGranularity, getDateTimeInputConfig } from '@/utilities/date';
import { type AssetWorkflowMetadata } from '@/types/asset';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { KeyboardKey } from '@/lib/keyboard';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

/**
 * Renders the metadata fields. The form they write into, and the bar that saves it, live outside this
 * section — see `metadata-panel-form.tsx`. That is what lets the section keep its natural height and
 * its open/close animation instead of stretching to the panel's bottom edge for a sticky bar.
 */
export const MetadataAccordion: FC<{
  multipleAssetsMetadata?: AssetWorkflowMetadata;
  selectedItems?: Array<{ id: string; name: string }>;
  disabled?: boolean;
  readOnly?: boolean;
  isUpload?: boolean;
}> = ({ multipleAssetsMetadata, selectedItems, disabled, isUpload, readOnly = false }) => {
  const tCommon = useTranslations('common');
  const tEmptyStates = useTranslations('emptyStates');
  const tInspectorMetadata = useTranslations('inspector.metadata');

  const {
    form,
    metadata,
    metadataListIsFetching,
    canViewPrivateMetadata,
    valuesVersion,
    markEdited,
    registerFieldRow,
    submit,
  } = useMetadataPanelForm();

  const getPublicMetadataOptions = (field: PublicMetadataField) => {
    let options: Array<{ value: string; label: string }> = [];
    if (field.value === null) {
      return options;
    }
    if (field.type === 'multi_select') {
      const fieldValue = field.value as Array<string>;
      options = fieldValue.map((value: string) => ({ value, label: value.charAt(0).toUpperCase() + value.slice(1) }));
    } else if (field.type === 'select') {
      const fieldValue = field.value as string;
      options = [
        {
          value: fieldValue,
          label: fieldValue,
        },
      ];
    }
    return options;
  };

  const getMetadataOptions = (field: MetadataField) => {
    let options: Array<{ value: string; label: string }> = [];
    if (Array.isArray(field.options)) {
      options = [];
    } else if (field.options?.choices) {
      options = Object.entries(field.options.choices).map(([value, label]) => ({ value, label: String(label) }));
    }
    return options;
  };

  const renderField = (field: MetadataField | PublicMetadataField) => {
    const Component = FieldComponents[field.type];
    if (!Component) {
      return null;
    }

    const options: Array<{ value: string; label: string }> = canViewPrivateMetadata
      ? getMetadataOptions(field as MetadataField)
      : getPublicMetadataOptions(field as PublicMetadataField);

    return (
      <FormField
        // The field's identity plus the reset counter — never the live form value. Keying on the live
        // value (DM-1674) remounted the input on the first keystroke and dropped focus (DM-2524);
        // `valuesVersion` only changes when the selection's data is reset, so typing never remounts.
        key={`${field.slug}-${valuesVersion}`}
        disabled={readOnly}
        name={field.slug}
        control={form.control}
        render={({ field: controllerField }) => (
          <FormItem
            ref={(element) => registerFieldRow(field.slug, element)}
            // `gap-2 space-y-0` rather than FormItem's default `space-y-2`: in Tailwind v4 `space-y-*`
            // puts a bottom margin on every child but the last, and Radix renders a hidden native
            // control as a sibling of its trigger (select, checkbox), which pushes the trigger out of
            // last place and leaves a phantom 8px under the field. `gap` only spaces in-flow flex
            // items, and that hidden control is absolutely positioned, so it cannot introduce one.
            // `gap-y-0` on the boolean branch: it used to carry `space-y-0`, so a wrapped row had no
            // vertical gap, and the base `gap-2` would otherwise introduce one.
            className={cn(
              // `min-w-0`: a flex item defaults to `min-width: auto`, so one long unbreakable value —
              // a select's chosen option, say — would widen this row and with it the whole panel,
              // however hard the control clips its own text.
              'flex min-w-0 gap-2 space-y-0',
              field.type === 'boolean' ? 'flex-row flex-wrap gap-x-3 gap-y-0' : 'flex-col',
            )}
          >
            <div className={cn('flex items-center gap-2', field.type === 'boolean' && 'grow')}>
              <FormLabel className="leading-4 wrap-anywhere">{field.name}</FormLabel>
              {field.description && (
                <TooltipProvider key={ulid()} delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild className="flex opacity-50 duration-150 hover:opacity-100">
                      <Info className="icon-3.5 text-text-gray" />
                    </TooltipTrigger>
                    <TooltipPortal>
                      <TooltipContent className="max-w-75">
                        <p>{field.description}</p>
                      </TooltipContent>
                    </TooltipPortal>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <FormControl>
              <Component
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === KeyboardKey.Enter && field.type !== 'text') {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }}
                {...controllerField}
                value={controllerField.value ?? ''}
                placeholder={
                  field.type === 'date'
                    ? getDateTimeInputConfig(DateTimeGranularity.Day).placeholder
                    : field.type === 'datetime'
                      ? undefined
                      : field.name
                }
                options={options}
                data-cy={`${field.slug}-metadata-field`}
                {...(field.type === 'boolean' &&
                (multipleAssetsMetadata || (isUpload && selectedItems && selectedItems.length > 1)) &&
                (controllerField.value === undefined || controllerField.value === null)
                  ? { checked: 'indeterminate' as const }
                  : { checked: controllerField.value })}
                // {...(field.type === 'datetime' && { hourCycle: 12 })}
                indeterminate={
                  (multipleAssetsMetadata ?? (isUpload && selectedItems && selectedItems.length > 1)) &&
                  (controllerField.value === undefined || controllerField.value === null)
                }
                onChange={(value: any) => {
                  const triggerDateValidation = () => {
                    queueMicrotask(() => {
                      void form.trigger(field.slug);
                    });
                  };

                  markEdited(field.slug);

                  if (field.type === 'datetime') {
                    const dateTimeInputLength = getDateTimeInputConfig(DateTimeGranularity.Second).length;
                    // format datetime value as Y-m-d\TH:i:sP
                    if (!value) {
                      controllerField.onChange('');
                      triggerDateValidation();
                      return;
                    }
                    if (typeof value === 'string') {
                      controllerField.onChange(value);
                      if (value.length === dateTimeInputLength || value.includes('T')) {
                        triggerDateValidation();
                      }
                      return;
                    }
                    controllerField.onChange(format(value, "yyyy-MM-dd'T'HH:mm:ssxxx"));
                    triggerDateValidation();
                  } else if (field.type === 'multi_select') {
                    const newValues = (value as Array<{ value: string; label: string }>).map((option) => option.value);
                    controllerField.onChange(newValues);
                  } else {
                    controllerField.onChange(value);
                    if (field.type === 'date') {
                      const dateInputLength = getDateTimeInputConfig(DateTimeGranularity.Day).length;
                      if (!value || (typeof value === 'string' && value.length === dateInputLength)) {
                        triggerDateValidation();
                      }
                    }
                  }
                }}
                disabled={readOnly}
                className={cn(
                  field.type === 'text' && 'w-full',
                  ['text', 'string', 'float', 'integer', 'select', 'date', 'datetime'].includes(field.type) &&
                    'bg-input-background focus-within:ring-offset-ui-panel',
                  ['text', 'string', 'float', 'integer', 'date', 'datetime'].includes(field.type) && 'pr-0!',
                )}
                // Add Spellcheck to text, string or multi_select fields
                {...(['text', 'string', 'multi_select'].includes(field.type) && { spellCheck: true })}
                // Include Input's `appendIcon` property with an "Clear" button only if field type is text, string, float, date or integer and if controllerField.value contains value, but remove it if readOnly is true
                {...(['text', 'string', 'float', 'integer', 'date', 'datetime'].includes(field.type) &&
                  !readOnly &&
                  controllerField.value && {
                    appendIcon: (
                      <Button
                        type="button"
                        onClick={() => {
                          markEdited(field.slug);
                          controllerField.onChange('');
                          if (field.type === 'date' || field.type === 'datetime') {
                            queueMicrotask(() => {
                              void form.trigger(field.slug);
                            });
                          }
                        }}
                        variant="ghost"
                        className="mx-0.5 min-h-full rounded-[7px] border-0 p-0.75 text-xs text-icon-base! duration-150 hover:bg-transparent! hover:text-text-base! focus-visible:bg-ui-base focus-visible:ring-0"
                        aria-label={tCommon('clearField')}
                      >
                        {tCommon('clear')}
                      </Button>
                    ),
                  })}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  };

  return (
    <AccordionItem value="metadata" disabled={disabled} className="min-w-0">
      {!isUpload && <AccordionTrigger disabled={disabled}>{tInspectorMetadata('customMetadata')}</AccordionTrigger>}
      <AccordionContent className="m-0 flex flex-col p-0">
        {metadataListIsFetching ? (
          <div className="space-y-5 p-3">
            {Array.from(Array(2)).map((_, index) => (
              <div className="flex flex-col items-center space-y-2 gap-y-2" key={index}>
                <div className="flex w-full items-center gap-x-2">
                  <Skeleton className={cn('order-1 h-5', index === 0 ? 'w-1/2' : 'w-1/3')} />
                  {index === 1 && <Skeleton className="order-2 size-4 rounded-full" />}
                </div>
                <Skeleton className="m-0! h-8 w-full" />
              </div>
            ))}
          </div>
        ) : metadata.length === 0 ? (
          <div className="flex h-40 flex-col items-center justify-center gap-y-2 p-3 text-text-gray-alternate">
            <RectangleEllipsis className="mx-auto " size={32} strokeWidth={1.5} />
            {tEmptyStates('noMetadata.title')}
          </div>
        ) : (
          // A real <form> element even though the save bar submits from outside it: Radix only gives a
          // Select its hidden native control — and therefore its form participation — when it finds a
          // `form` ancestor, and Enter inside a field should submit rather than do nothing.
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
            className="flex flex-col space-y-6 p-3"
          >
            {metadata.map((field) => renderField(field))}
          </form>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};
